import React, { useState, useEffect, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { Loader2, ScanLine, Package, Plus, Pencil } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import WhatsAppConfig from "@/pages/panel/WhatsAppConfig";
import CorreosConfig from "@/pages/panel/CorreosConfig";
import { t } from "@/lib/i18n"; // Prompt L

export default function Configuracion() {
  const [umbral, setUmbral] = useState(3);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/configuracion").then(({ data }) => {
      setUmbral(data.umbral_legibilidad);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const guardar = async () => {
    setSaving(true);
    try {
      await api.put("/configuracion", { umbral_legibilidad: umbral });
      toast.success(t("Configuración guardada"));
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (loading) return <div className="p-6"><Loader2 className="h-6 w-6 animate-spin text-ink-3" /></div>;

  return (
    <div className="p-4 lg:p-8 max-w-3xl space-y-5">
      <div>
        <h1 className="font-display font-bold text-[26px] lg:text-[30px] leading-[1.05] text-ink" data-testid="page-title">{t("Configuración")}</h1>
        <p className="text-sm text-ink-3">{t("Escáner con IA, categorías de gasto, correos y WhatsApp")}</p>
      </div>

      <Tabs defaultValue="general">
        <TabsList className="bg-paper">
          <TabsTrigger value="general" data-testid="config-tab-general">{t("General")}</TabsTrigger>
          <TabsTrigger value="correos" data-testid="config-tab-correos">{t("Correos")}</TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="config-tab-whatsapp">{t("WhatsApp")}</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-6">
          <div className="bg-white rounded-xl border p-6">
            <div className="flex items-center gap-2 mb-1">
              <ScanLine className="h-5 w-5 text-ink" />
              <h2 className="font-display font-semibold text-[18px] text-ink">{t("Umbral de legibilidad")}</h2>
            </div>
            <p className="text-sm text-ink-3 mb-4">{t("Un papel se aprueba solo si su legibilidad es mayor o igual a este nivel (1 a 5).")}</p>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  data-testid={`umbral-${n}`}
                  onClick={() => setUmbral(n)}
                  className={cn("h-14 w-14 rounded-xl border-2 text-lg font-bold transition-all", umbral === n ? "border-ink bg-accent text-ink" : "border-line text-ink-3")}
                >
                  {n}
                </button>
              ))}
            </div>
            <Button data-testid="config-guardar" onClick={guardar} disabled={saving} className="mt-6 h-11 bg-brand hover:bg-brand-dark text-white px-8">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Guardar")}
            </Button>
          </div>

          <CategoriasGasto />
        </TabsContent>

        <TabsContent value="correos">
          <CorreosConfig />
        </TabsContent>

        <TabsContent value="whatsapp">
          <WhatsAppConfig />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CategoriasGasto() {
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const cargar = useCallback(async () => {
    const { data } = await api.get("/categorias-gasto");
    setCats(data);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <div className="bg-white rounded-xl border p-6">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5 text-ink" />
          <h2 className="font-display font-semibold text-[18px] text-ink">{t("Categorías de insumos y gastos")}</h2>
        </div>
        <Button data-testid="categoria-nueva-btn" size="sm" className="bg-brand hover:bg-brand-dark text-white" onClick={() => { setEditing(null); setOpen(true); }}><Plus className="h-4 w-4 mr-1" />{t(" Nueva")}</Button>
      </div>
      <p className="text-sm text-ink-3 mb-4">{t("Las categorías \"habituales\" no requieren aprobación para registrar un gasto.")}</p>

      <div className="divide-y border rounded-lg overflow-hidden">
        {cats.length === 0 && <p className="p-4 text-sm text-ink-3">{t("Sin categorías")}</p>}
        {cats.map((c) => (
          <div key={c.id} data-testid={`categoria-fila-${c.id}`} className={cn("flex items-center justify-between px-4 py-3", !c.activo && "opacity-50")}>
            <div>
              <p className="font-semibold text-ink">{c.nombre}</p>
              <p className="text-xs text-ink-3">{c.habitual ? t("Habitual (sin aprobación)") : t("Requiere aprobación")} · {c.activo ? t("Activa") : t("Inactiva")}</p>
            </div>
            <Button data-testid={`categoria-editar-${c.id}`} variant="ghost" size="sm" onClick={() => { setEditing(c); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>

      {open && <CategoriaDialog categoria={editing} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); cargar(); }} />}
    </div>
  );
}

function CategoriaDialog({ categoria, onClose, onSaved }) {
  const [nombre, setNombre] = useState(categoria?.nombre || "");
  const [habitual, setHabitual] = useState(categoria?.habitual || false);
  const [activo, setActivo] = useState(categoria ? categoria.activo : true);
  const [saving, setSaving] = useState(false);

  const guardar = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { nombre, habitual, activo };
      if (categoria) await api.put(`/categorias-gasto/${categoria.id}`, payload);
      else await api.post("/categorias-gasto", payload);
      toast.success(categoria ? t("Categoría actualizada") : t("Categoría creada"));
      onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle>{categoria ? t("Editar categoría") : t("Nueva categoría")}</DialogTitle></DialogHeader>
        <form onSubmit={guardar} className="space-y-4">
          <div><Label className="eyebrow">{t("Nombre *")}</Label><Input data-testid="categoria-nombre-input" value={nombre} onChange={(e) => setNombre(e.target.value)} className="mt-1 h-11" required /></div>
          <label className="flex items-center gap-2 text-sm text-ink-2"><input data-testid="categoria-habitual-check" type="checkbox" checked={habitual} onChange={(e) => setHabitual(e.target.checked)} className="h-4 w-4" />{t(" Habitual (no requiere aprobación)")}</label>
          {categoria && <label className="flex items-center gap-2 text-sm text-ink-2"><input data-testid="categoria-activo-check" type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)} className="h-4 w-4" />{t(" Activa")}</label>}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>{t("Cancelar")}</Button>
            <Button type="submit" data-testid="categoria-guardar" className="bg-brand hover:bg-brand-dark text-white" disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Guardar")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
